import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { customerAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useConfig } from '../../context/ConfigContext';
import toast from 'react-hot-toast';
import {
  UserIcon,
  KeyIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// Tab definitions
const tabs = [
  { id: 'account', name: 'Account information', icon: UserIcon },
  { id: 'password', name: 'Password and Security', icon: KeyIcon },
  { id: 'application', name: 'Application setting', icon: Cog6ToothIcon },
  { id: 'danger', name: 'Danger zone', icon: ExclamationTriangleIcon },
];

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState('account');
  const { user, logout } = useAuth();
  const { config } = useConfig();
  const queryClient = useQueryClient();

  // Account information state (read-only user info + editable profile fields)
  const [accountData, setAccountData] = useState({
    company_name: '',
    company_website: '',
    contact_email: '',
    contact_name: '',
  });

  // Password state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  // Application settings state (read-only from config)
  const [appSettings, setAppSettings] = useState({
    brand_name: '',
    document_limit: 100,
    primary_color: '#007073',
    max_file_size: 50,
  });

  const { data: profile } = useQuery('profile', customerAPI.getProfile);

  useEffect(() => {
    if (profile) {
      setAccountData({
        company_name: profile.company_name || '',
        company_website: profile.company_website || '',
        contact_email: profile.contact_email || '',
        contact_name: profile.contact_name || '',
      });
    }
  }, [profile]);

  useEffect(() => {
    if (config) {
      setAppSettings({
        brand_name: profile?.company_name || config.brand_name || '',
        document_limit: config.max_documents || 100,
        primary_color: config.primary_color || '#007073',
        max_file_size: config.max_file_size_mb || 50,
      });
    }
  }, [config, profile]);

  const updateProfileMutation = useMutation(
    (data) => customerAPI.updateProfile(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('profile');
        toast.success('Profile updated successfully!');
      },
      onError: (error) => {
        const message = error.response?.data?.detail || 'Failed to update profile';
        toast.error(typeof message === 'string' ? message : 'Failed to update profile');
      },
    }
  );

  const changePasswordMutation = useMutation(
    (data) => customerAPI.changePassword(data),
    {
      onSuccess: () => {
        toast.success('Password changed successfully!');
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: '',
        });
      },
      onError: (error) => {
        const message = error.response?.data?.detail || 'Failed to change password';
        toast.error(typeof message === 'string' ? message : 'Failed to change password');
      },
    }
  );

  const handleAccountSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!accountData.company_name || !accountData.contact_email) {
      toast.error('Company name and contact email are required');
      return;
    }

    updateProfileMutation.mutate({
      company_name: accountData.company_name,
      company_website: accountData.company_website || null,
      contact_email: accountData.contact_email,
      contact_name: accountData.contact_name || null,
    });
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    changePasswordMutation.mutate({
      current_password: passwordData.current_password,
      new_password: passwordData.new_password,
    });
  };

  const handleAppSettingsSubmit = (e) => {
    e.preventDefault();
    // App settings are mostly read-only (determined by plan)
    // Only brand name can be updated via profile
    updateProfileMutation.mutate({
      company_name: appSettings.brand_name,
      company_website: accountData.company_website || null,
      contact_email: accountData.contact_email,
      contact_name: accountData.contact_name || null,
    });
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      toast.error('Account deletion is not yet implemented. Please contact support.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="font-source">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 font-arsenal">Settings</h1>
        <p className="mt-1 text-gray-600">
          Manage your account settings and preferences.
        </p>
      </div>

      {/* Card */}
      <div className="bg-white rounded-xl border border-gray-100">
        {/* Tabs */}
        <div className="border-b border-gray-100">
          <nav className="flex -mb-px overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-cleona-600 text-cleona-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Account Information Tab */}
          {activeTab === 'account' && (
            <form onSubmit={handleAccountSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    value={user?.username || ''}
                    disabled
                    placeholder="Your Username"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    placeholder="Your Email"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={accountData.company_name}
                    onChange={(e) => setAccountData({ ...accountData, company_name: e.target.value })}
                    placeholder="Your Company Name"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    value={accountData.contact_email}
                    onChange={(e) => setAccountData({ ...accountData, contact_email: e.target.value })}
                    placeholder="Contact Email"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tenant ID
                  </label>
                  <input
                    type="text"
                    value={user?.tenantId || ''}
                    disabled
                    placeholder="Your Tenant ID"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 font-mono text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Created
                  </label>
                  <input
                    type="text"
                    value={formatDate(profile?.created_at)}
                    disabled
                    placeholder="N/A"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => {
                    if (profile) {
                      setAccountData({
                        company_name: profile.company_name || '',
                        company_website: profile.company_website || '',
                        contact_email: profile.contact_email || '',
                        contact_name: profile.contact_name || '',
                      });
                    }
                  }}
                  className="px-8 py-3 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateProfileMutation.isLoading}
                  className="px-8 py-3 bg-cleona-600 text-white rounded-lg hover:bg-cleona-700 transition-colors font-medium disabled:opacity-50"
                >
                  {updateProfileMutation.isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          )}

          {/* Password and Security Tab */}
          {activeTab === 'password' && (
            <form onSubmit={handlePasswordSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    placeholder="Your Current Password"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="Your New Password"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    placeholder="Confirm New Password"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => setPasswordData({ current_password: '', new_password: '', confirm_password: '' })}
                  className="px-8 py-3 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={changePasswordMutation.isLoading || !passwordData.current_password || !passwordData.new_password}
                  className="px-8 py-3 bg-cleona-600 text-white rounded-lg hover:bg-cleona-700 transition-colors font-medium disabled:opacity-50"
                >
                  {changePasswordMutation.isLoading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          )}

          {/* Application Settings Tab */}
          {activeTab === 'application' && (
            <form onSubmit={handleAppSettingsSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Brand Name
                  </label>
                  <input
                    type="text"
                    value={appSettings.brand_name}
                    onChange={(e) => setAppSettings({ ...appSettings, brand_name: e.target.value })}
                    placeholder="Your Brand Name"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document Limit
                  </label>
                  <input
                    type="text"
                    value={`${appSettings.document_limit} documents`}
                    disabled
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Determined by your subscription plan</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full border-2 border-white shadow"
                      style={{ backgroundColor: appSettings.primary_color }}
                    />
                    <input
                      type="text"
                      value={appSettings.primary_color}
                      disabled
                      className="flex-1 px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Configure in Website Chat settings</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max File Size
                  </label>
                  <input
                    type="text"
                    value={`${appSettings.max_file_size}MB`}
                    disabled
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Determined by your subscription plan</p>
                </div>
              </div>

              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  className="px-8 py-3 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateProfileMutation.isLoading}
                  className="px-8 py-3 bg-cleona-600 text-white rounded-lg hover:bg-cleona-700 transition-colors font-medium disabled:opacity-50"
                >
                  {updateProfileMutation.isLoading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          )}

          {/* Danger Zone Tab */}
          {activeTab === 'danger' && (
            <div className="text-center py-12">
              {/* Illustration */}
              <div className="relative w-32 h-32 mx-auto mb-8">
                <div className="w-24 h-24 bg-gradient-to-br from-cleona-200 to-cleona-400 rounded-full mx-auto" />
                <div className="absolute bottom-0 right-4 w-10 h-10 bg-cleona-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Delete Account</h2>
              <p className="text-gray-600 max-w-md mx-auto mb-8">
                Once you delete your account, there is no going back. This will permanently delete your account and all associated data.
              </p>

              <button
                onClick={handleDeleteAccount}
                className="px-8 py-3 bg-cleona-600 text-white rounded-lg hover:bg-cleona-700 transition-colors font-medium"
              >
                Delete Account
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
